// FreshnessBanner — LIVE / STALE / DARK strip with the two-clock backfill split.
//
// Load-bearing: on historical_backfill render the split ("events from <event>,
// received <received>") — NEVER a bare "20 h ago". On DARK the caller dims +
// freezes content (resolution.dim / .freeze).
import React from "react";

import type { FreshnessResolution } from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { FRESHNESS_COLORS } from "./honestyTokens";

export interface FreshnessBannerProps {
  resolution: FreshnessResolution;
}

const STATE_LABELS = { LIVE: "Live", STALE: "Stale", DARK: "Dark" } as const;

export const FreshnessBanner: React.FC<FreshnessBannerProps> = ({ resolution }) => {
  const { translate } = useTranslation("machineEvidence");
  const tone = FRESHNESS_COLORS[resolution.state];
  // Two-clock backfill sentence — CLIENT chrome with the two formatted dates
  // (payload-derived numerals) interpolated in; never a bare "20 h ago".
  const split: string | null = resolution.twoClock
    ? (translate("freshness.backfillSplit", {
        event: resolution.twoClock.eventTimeFormatted,
        received: resolution.twoClock.receivedAtFormatted,
        defaultValue: "events from {{event}}, received {{received}}",
      }) as string)
    : null;
  const stateLabel = translate(`freshness.state.${resolution.state.toLowerCase()}`, {
    defaultValue: STATE_LABELS[resolution.state],
  }) as string;

  return (
    <div
      className={`msc-fresh msc-fresh-${resolution.state.toLowerCase()}`}
      data-freshness-state={resolution.state}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px",
        borderRadius: dt.radiusSm,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.fg,
        fontSize: 12,
      }}
    >
      <span
        aria-hidden
        className={resolution.state === "LIVE" ? "msc-fresh-dot-pulse" : undefined}
        style={{ width: 8, height: 8, borderRadius: "50%", background: tone.fg }}
      />
      <span style={{ fontFamily: dt.fontMono, fontWeight: 600, letterSpacing: "0.03em" }}>
        {stateLabel}
      </span>
      {split ? (
        <span style={{ color: dt.textSecondary }}>{split}</span>
      ) : resolution.state === "DARK" ? (
        <span style={{ color: dt.textSecondary }}>
          {translate("freshness.darkNote", { defaultValue: "feed dark — counts frozen" }) as string}
        </span>
      ) : null}
    </div>
  );
};

export default FreshnessBanner;
